(() => {
  const configNode = document.getElementById('prism-config');
  if (!configNode || !window.PrismEntries) return;
  const config = JSON.parse(configNode.textContent);
  const engine = window.PrismEntries;
  const t = (key) => config.strings[key] || key;
  const format = (n) => new Intl.NumberFormat(document.documentElement.lang || 'en').format(n);
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: state.cart?.currency || config.currency || 'USD',
  }).format(cents / 100);
  const state = { cart: null, campaign: null, plan: 1, result: null, sequence: 0, busy: false, cache: new Map() };
  const products = config.boosters || [];
  let refreshTimer;
  let pendingCartToast = false;
  let priorFocus;
  const demoTimerSeconds = 75 * 60;
  const session = {
    read(key) { try { return sessionStorage.getItem('prism-' + key); } catch { return this[key]; } },
    write(key, value) { this[key] = String(value); try { sessionStorage.setItem('prism-' + key, String(value)); } catch {} },
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const setText = (selector, text) => document.querySelectorAll(selector).forEach((element) => {
    if (element.textContent !== text) element.textContent = text;
  });
  const route = (suffix) => config.root + suffix;

  async function request(url, options = {}) {
    const response = await fetch(url, { ...options, signal: AbortSignal.timeout(15000) });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || data.message || t('request_error'));
    return data;
  }

  function productIds(cart) {
    return [...new Set([
      ...(cart.items || []).map((line) => String(line.product_id)),
      ...products.map((product) => String(product.id)),
      ...Array.from(document.querySelectorAll('prism-entry[data-product-id]'), (element) => element.dataset.productId),
    ])].filter(Boolean).sort();
  }

  async function rules(ids) {
    const key = ids.join(',');
    const cached = state.cache.get(key);
    if (cached && Date.now() - cached.time < 60000) return cached.promise;
    const promise = (async () => {
      const chunks = [];
      for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
      if (!chunks.length) chunks.push(null);
      const results = await Promise.all(chunks.map((chunk) => request(config.endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query PrismRules($platform: String!, $platformId: String!${chunk ? ', $productIds: [String!]' : ''}) {
            organization(platform: $platform, platformId: $platformId) { plan }
            sweepstakeDetailsByProductId(platform: $platform, platformId: $platformId) {
              id isActive startDate endDate entryManagement productsAllowedByDefault bonusEntriesDefault
              calculateWithDiscount bonusSpendingEnabledProductOnly membershipProductMultOverride
              scheduledBonusActive scheduledBonusValue scheduledBonusStart scheduledBonusEnd
              ${chunk ? 'products(productIds: $productIds, take: 100) { productId productAllowed productBonusEntries productMultiplier orderMultiplierAllowed orderMultiplier }' : ''}
            }
          }`,
          variables: { platform: 'Shopify', platformId: config.shopId,
            ...(chunk ? { productIds: chunk.map((id) => 'gid://shopify/Product/' + id) } : {}) },
        }),
      })));
      const campaigns = results.map((response) => response.data?.sweepstakeDetailsByProductId?.find((campaign) => String(campaign.id) === String(config.campaignId)));
      if (campaigns.some((campaign) => !campaign)) throw new Error(t('unavailable'));
      return { campaign: { ...campaigns[0], products: campaigns.flatMap((campaign) => campaign.products || []) },
        plan: Number(results[0].data.organization?.plan || 1) };
    })();
    state.cache.set(key, { time: Date.now(), promise });
    try { return await promise; } catch (error) { state.cache.delete(key); throw error; }
  }

  function scheduleRefresh(cart) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refresh(cart), 70);
  }

  async function refresh(cart) {
    let pending = performRefresh(cart);
    state.pendingRefresh = pending;
    let ready = await pending;
    while (pending !== state.pendingRefresh) {
      pending = state.pendingRefresh;
      ready = await pending;
    }
    return ready;
  }

  async function performRefresh(cart) {
    const sequence = ++state.sequence;
    try {
      const nextCart = cart?.items ? cart : await request(route('cart.js'));
      const settings = await rules(productIds(nextCart));
      const result = engine.calculate(nextCart, settings.campaign, settings.plan);
      if (sequence !== state.sequence) return false;
      Object.assign(state, { cart: nextCart, result, ...settings });
      render();
      return true;
    } catch {
      if (sequence !== state.sequence) return false;
      state.result = null;
      document.querySelectorAll('prism-entry').forEach((element) => { element.classList.remove('pv-entry--boosted'); element.classList.add('pv-entry--excluded'); });
      document.querySelectorAll('[data-prism-cart-boost], [data-prism-order-booster-row]').forEach((element) => { element.hidden = true; });
      setText('[data-prism-total], [data-prism-value], prism-entry', t('unavailable'));
      setText('[data-prism-status]', t('unavailable'));
      document.querySelectorAll('[data-prism-retry]').forEach((button) => { button.hidden = false; });
      document.querySelectorAll('[data-prism-add], [data-prism-finish]').forEach((button) => { button.disabled = true; });
      return false;
    }
  }

  function standalone(element) {
    const quantityInput = element.dataset.context === 'product'
      ? element.closest('product-info')?.querySelector('input[name="quantity"]') : null;
    const quantity = Math.max(1, Number(quantityInput?.value || 1));
    const cents = Number(element.dataset.price || 0) * quantity;
    const cart = { original_total_price: cents, total_price: cents,
      items: [{ product_id: element.dataset.productId, quantity, original_line_price: cents, final_line_price: cents }] };
    return engine.calculate(cart, state.campaign, state.plan).lines[0];
  }

  function render() {
    const result = state.result;
    if (!result) return;
    document.querySelectorAll('prism-entry').forEach((element) => {
      const line = element.dataset.context === 'line' ? result.lines.find((line) => line.key === element.dataset.key) : standalone(element);
      element.classList.toggle('pv-entry--excluded', !line?.eligible);
      if (!line?.eligible) { element.textContent = t('not_eligible'); return; }
      const management = typeof state.campaign.entryManagement === 'string'
        ? JSON.parse(state.campaign.entryManagement) : state.campaign.entryManagement;
      const round = management.roundUp === false ? Math.floor : Math.ceil;
      const multiplier = round(result.rate * line.multiplier);
      if (!element.querySelector('.pv-entry-number')) {
        const amount = node('span', 'pv-entry-amount');
        amount.append(node('strong', 'pv-entry-number'), document.createTextNode(' '), node('span', 'pv-entry-label', t('entries')));
        const badge = node('span', 'pv-entry-multiplier pv-multiplier-badge');
        badge.append(node('strong', 'pv-multiplier-number'));
        element.replaceChildren(amount, badge);
      }
      element.querySelector('.pv-entry-number').textContent = format(line.entries);
      const badge = element.querySelector('.pv-entry-multiplier');
      badge.hidden = multiplier <= 1;
      badge.querySelector('.pv-multiplier-number').textContent = format(multiplier) + '×';
      badge.title = `${format(result.rate * line.multiplier)} ${t('entries')} / ${money(result.dollars * 100)}`;
      badge.setAttribute('aria-label', badge.title);
      element.classList.toggle('pv-entry--boosted', multiplier > 1);
    });
    setText('[data-prism-total]', format(result.total));
    setText('[data-prism-value="purchase"]', format(result.lines.reduce((sum, line) => sum + line.entries, 0)));
    setText('[data-prism-value="rounding"]', (result.roundingAdjustment > 0 ? '+' : '') + format(result.roundingAdjustment));
    setText('[data-prism-value="spending"]', '+' + format(result.spendingBonus));
    setText('[data-prism-value="multiplier"]', format(result.orderMultiplier) + '×');
    document.querySelectorAll('[data-prism-cart-boost], [data-prism-order-booster-row]').forEach((element) => { element.hidden = result.orderMultiplier <= 1; });
    setText('[data-prism-cart-boost-value]', format(result.orderMultiplier) + '×');
    setText('[data-prism-money]', money(state.cart.total_price));
    setText('[data-prism-count]', format(state.cart.item_count || 0));
    setText('[data-prism-rate]', `${format(result.rate)} ${t('entries')} / ${money(result.dollars * 100)}`);
    setText('[data-prism-status]', t('live_rules'));
    document.querySelectorAll('[data-prism-rounding-row]').forEach((element) => { element.hidden = result.roundingAdjustment === 0; });
    document.querySelectorAll('[data-prism-retry]').forEach((button) => { button.hidden = true; });
    document.querySelectorAll('[data-prism-finish]').forEach((button) => { button.disabled = !(state.cart.item_count > 0); });
    document.querySelectorAll('[data-prism-progress]').forEach((element) => renderProgress(element));
    document.querySelectorAll('[data-prism-offers]').forEach((element) => renderOffers(element));
  }

  function renderProgress(container) {
    const { thresholds, thresholdCents, nextThreshold, thresholdBasis } = state.result;
    container.replaceChildren();
    if (!thresholds.length) {
      container.append(node('p', 'pv-muted', t('no_thresholds')));
      return;
    }
    const last = thresholds.at(-1).cents;
    const progress = node('progress', 'pv-progress-track');
    progress.max = Math.max(1, last);
    progress.value = Math.min(thresholdCents, last);
    progress.setAttribute('aria-label', t('bonus_progress'));
    const milestones = node('div', 'pv-milestone-stages');
    let previous = 0;
    thresholds.forEach((threshold) => {
      const stage = node('div', 'pv-milestone-stage' + (threshold.reached ? ' is-reached' : ''));
      const span = Math.max(1, threshold.cents - previous);
      const fill = Math.max(0, Math.min(1, (thresholdCents - previous) / span));
      stage.style.flexGrow = String(span);
      const track = node('div', 'pv-milestone-track');
      const filled = node('span', 'pv-milestone-fill');
      filled.style.width = `${fill * 100}%`;
      const ball = node('span', 'pv-milestone-ball', threshold.reached ? '✓' : String(milestones.children.length + 1));
      ball.setAttribute('aria-hidden', 'true');
      track.append(filled, ball);
      const label = node('div', 'pv-milestone-label');
      label.append(node('strong', '', money(threshold.cents)), node('span', '', `+${format(threshold.entries)} ${t('entries')}`), node('small', 'pv-milestone-state', t(threshold.reached ? 'bonus_unlocked' : 'bonus_locked')));
      stage.append(track, label);
      milestones.append(stage);
      previous = threshold.cents;
    });
    progress.className = 'visually-hidden';
    container.classList.toggle('is-complete', !nextThreshold);
    const copy = nextThreshold
      ? t('next_bonus').replace('{amount}', money(nextThreshold.cents - thresholdCents)).replace('{entries}', format(nextThreshold.entries))
      : t('all_bonuses');
    container.append(milestones, progress, node('p', 'pv-progress-copy', copy), node('small', 'pv-muted', t('basis_' + thresholdBasis)));
  }

  function offers() {
    if (!state.result) return [];
    return products.map((product) => {
      const rule = state.campaign.products.find((rule) => engine.id(rule.productId) === String(product.id));
      const factor = state.plan >= 3 && rule?.orderMultiplierAllowed ? Number(rule.orderMultiplier) : 1;
      const projection = engine.calculate(engine.withProduct(state.cart, product), state.campaign, state.plan);
      return { ...product, factor, projection, upgrade: factor > state.result.orderMultiplier };
    }).filter((product) => product.factor > 1).sort((a, b) => a.factor - b.factor);
  }

  function renderOffers(container) {
    const options = offers();
    container.replaceChildren();
    if (!options.length) {
      container.append(node('p', 'pv-muted', t('no_boosters')));
      return;
    }
    options.forEach((product) => {
      const card = node('div', 'pv-offer');
      if (product.image) {
        const image = node('img'); image.src = product.image; image.alt = product.title; image.width = 220; image.height = 220; image.loading = 'lazy';
        card.append(image);
      }
      card.append(node('strong', 'pv-offer-factor pv-multiplier-badge', `${product.factor}×`), node('span', 'pv-offer-price', money(product.price)));
      const button = node('button', 'pv-button pv-button--small', !product.available ? t('sold_out') : product.upgrade ? t('add_boost') : t('already_boosted'));
      button.type = 'button'; button.dataset.prismAdd = String(product.variantId);
      button.disabled = state.busy || !product.available || !product.upgrade;
      button.setAttribute('aria-label', `${button.textContent} ${product.factor}× · ${money(product.price)}`);
      card.append(button, node('small', 'pv-offer-result', product.upgrade ? `${format(product.projection.total)} ${t('entries_total')}` : t('highest_applies')));
      container.append(card);
    });
  }

  function showCartToast() {
    pendingCartToast = false;
    const toast = document.querySelector('[data-prism-cart-toast]');
    if (!toast) return;
    toast.hidden = false;
    setText('[data-prism-toast-message]', t('cart_added'));
  }

  const dialog = document.getElementById('prism-upsell');
  dialog?.addEventListener('click', (event) => { if (event.target === dialog && !state.busy) dialog.close(); });
  dialog?.addEventListener('cancel', (event) => { if (state.busy) event.preventDefault(); });
  dialog?.addEventListener('close', () => {
    document.body.classList.remove('pv-modal-open');
    priorFocus?.focus?.();
    if (pendingCartToast) showCartToast();
  });

  async function showUpsell(automatic = false) {
    if (!dialog || dialog.open) return false;
    if (!await refresh()) return false;
    if (dialog.open) return true;
    priorFocus = document.activeElement;
    document.querySelector('[data-prism-cart-toast]')?.setAttribute('hidden', '');
    dialog.showModal();
    document.body.classList.add('pv-modal-open');
    dialog.querySelector('button:not([disabled])')?.focus();
    return true;
  }

  async function afterAdd(response, source) {
    const quickAdd = source?.closest('quick-add-modal');
    if (quickAdd) { quickAdd.hide(true); await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))); }
    pendingCartToast = true;
    if (await showUpsell(true)) return;
    showCartToast();
  }

  function cartSections() {
    const items = document.querySelector('cart-items');
    return items?.getSectionsToRender?.() || [];
  }

  async function updateCartDOM(response) {
    const sections = cartSections();
    if (!sections.length) return;
    const markup = response.sections || await request(`${config.cartUrl}?sections=${sections.map((section) => section.section).join(',')}`);
    for (const section of sections) {
      if (!markup[section.section]) throw new Error('Section unavailable');
      const parsed = new DOMParser().parseFromString(markup[section.section], 'text/html');
      const target = document.getElementById(section.id);
      const source = parsed.querySelector(section.selector);
      if (target && source) (target.querySelector(section.selector) || target).innerHTML = source.innerHTML;
    }
    const cart = await request(route('cart.js'));
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    await refresh(cart);
  }

  async function addBooster(variantId) {
    if (state.busy) return;
    state.busy = true;
    let added = false;
    setText('[data-prism-error]', '');
    document.querySelectorAll('[data-prism-add]').forEach((button) => { button.disabled = true; });
    try {
      if (!await refresh()) throw new Error(t('unavailable'));
      const offer = offers().find((product) => String(product.variantId) === variantId);
      if (!offer?.available || !offer.upgrade) return;
      const sections = cartSections();
      const response = await request(route('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: offer.variantId, quantity: 1 }],
          ...(sections.length ? { sections: sections.map((section) => section.section), sections_url: config.cartUrl } : {}) }) });
      added = true;
      pendingCartToast = true;
      if (sections.length) await updateCartDOM(response);
      else await refresh();
      setText('[data-prism-error]', t('boost_added'));
      // Keep an open popup updated after accepting an offer; otherwise open it.
      if (!dialog?.open && !await showUpsell(!sections.length)) showCartToast();
    } catch (error) {
      if (added) {
        if (!dialog?.open) showCartToast();
        setText('[data-prism-error]', t('boost_added'));
        scheduleRefresh();
      }
      else setText('[data-prism-error]', error.message || t('request_error'));
    } finally {
      state.busy = false;
      if (state.result) document.querySelectorAll('[data-prism-offers]').forEach(renderOffers);
    }
  }

  const finishDialog = document.getElementById('prism-finish');
  finishDialog?.addEventListener('click', (event) => { if (event.target === finishDialog) finishDialog.close(); });
  finishDialog?.addEventListener('close', () => priorFocus?.focus?.());
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    if (button.hasAttribute('data-prism-toast-close')) button.closest('[data-prism-cart-toast]').hidden = true;
    if (button.hasAttribute('data-prism-open')) await showUpsell();
    if (button.hasAttribute('data-prism-close') && !state.busy) dialog?.close();
    if (button.hasAttribute('data-prism-add')) await addBooster(button.dataset.prismAdd);
    if (button.hasAttribute('data-prism-retry')) { state.cache.clear(); await refresh(); }
    if (button.hasAttribute('data-prism-finish') && await refresh()) { priorFocus = button; finishDialog?.showModal(); }
    if (button.hasAttribute('data-prism-finish-close')) finishDialog?.close();
    if (button.hasAttribute('data-prism-reset') && !state.busy) {
      state.busy = true; button.disabled = true;
      try { await request(route('cart/clear.js'), { method: 'POST' }); window.location.assign(config.cartUrl); }
      catch { setText('[data-prism-reset-error]', t('request_error')); button.disabled = false; }
      finally { state.busy = false; }
    }
    if (button.hasAttribute('data-prism-sample')) {
      const target = document.getElementById(button.dataset.prismSample);
      if (target) { target.hidden = !target.hidden; button.setAttribute('aria-expanded', String(!target.hidden)); }
    }
    if (button.hasAttribute('data-prism-timer-reset')) { session.write('timer-end', Date.now() + demoTimerSeconds * 1000); tick(); }
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('product-info input[name="quantity"]')) { if (state.result) render(); else scheduleRefresh(); }
  });

  customElements.define('prism-entry', class extends HTMLElement { connectedCallback() { scheduleRefresh(state.cart); } });
  new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((element) => element.nodeType === 1 &&
      (element.matches('[data-prism-progress], [data-prism-offers]') || element.querySelector('[data-prism-progress], [data-prism-offers]'))))) scheduleRefresh();
  }).observe(document.body, { childList: true, subtree: true });
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { scheduleRefresh(event.cartData); });
    subscribe(PUB_SUB_EVENTS.variantChange, () => scheduleRefresh(state.cart));
  }
  document.addEventListener('shopify:section:load', () => scheduleRefresh());
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });
  function updateFlip(card, value) {
    const next = String(value).padStart(2, '0');
    if (card.dataset.value === next) return;
    const previous = card.dataset.value;
    card.classList.remove('is-flipping');
    const staticTop = card.querySelector('.pv-flip-static-top span');
    const staticBottom = card.querySelector('.pv-flip-static-bottom span');
    staticTop.textContent = next;
    staticBottom.textContent = previous;
    card.querySelector('.pv-flip-top span').textContent = previous;
    card.querySelector('.pv-flip-bottom span').textContent = next;
    card.dataset.value = next;
    const settle = () => {
      staticBottom.textContent = card.dataset.value;
      card.classList.remove('is-flipping');
    };
    card.onanimationend = (event) => {
      if (event.target.matches('.pv-flip-bottom')) settle();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.hidden) {
      settle();
    } else {
      // Only the two moving halves transform; static faces stay pixel-aligned.
      void card.offsetWidth;
      card.classList.add('is-flipping');
    }
  }
  let lastBoundary = '';
  function tick() {
    const timers = document.querySelectorAll('[data-prism-timer]');
    if (timers.length) {
      if (!session.read('timer-end') || session.read('timer-duration') !== String(demoTimerSeconds)) {
        session.write('timer-end', Date.now() + demoTimerSeconds * 1000);
        session.write('timer-duration', demoTimerSeconds);
      }
      const seconds = Math.max(0, Math.ceil((Number(session.read('timer-end')) - Date.now()) / 1000));
      const minutesText = String(Math.floor(seconds / 60)).padStart(2, '0');
      const secondsText = String(seconds % 60).padStart(2, '0');
      setText('[data-prism-timer]', `${minutesText}:${secondsText}`);
      const units = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
      document.querySelectorAll('[data-prism-flip]').forEach((card) => updateFlip(card, units[card.dataset.prismFlip]));
      setText('[data-prism-bonus-heading]', t(seconds ? 'bonus_timer_title' : 'bonus_timer_ended'));
      document.querySelectorAll('[data-prism-timer-fill]').forEach((fill) => { fill.style.width = `${Math.min(100, seconds / demoTimerSeconds * 100)}%`; });
      document.querySelectorAll('[data-prism-bonus-banner]').forEach((banner) => banner.classList.toggle('is-ended', seconds === 0));
    }
    if (state.campaign) {
      const boundary = [state.campaign.scheduledBonusStart, state.campaign.scheduledBonusEnd, state.campaign.endDate]
        .filter(Boolean).map((time) => Date.now() >= Number(time)).join(',');
      if (lastBoundary && boundary !== lastBoundary) { state.cache.clear(); scheduleRefresh(); }
      lastBoundary = boundary;
    }
  }
  setInterval(tick, 1000);
  setInterval(() => { if (!document.hidden) scheduleRefresh(); }, 60000);
  window.PrismDemo = { afterAdd, refresh, showUpsell };
  tick();
  refresh();
})();
